import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { ChangeHistory } from '@/common/components/ChangeHistory'
import { FormTransformers } from '@/common/utils/formTransformers'
import { RequestedReview } from '@/features/establishmentLeadsFunnel/components/RequestedReview'
import { Checklist } from '@/features/privateCompanyLeadsFunnel/components/Checklist'
import { AddressSection } from '@/features/publicCompanyLeadsFunnel/components/AddressSection'
import { BiddingInformationSection } from '@/features/publicCompanyLeadsFunnel/components/BiddingInformationSection'
import { GeneralInformationSection } from '@/features/publicCompanyLeadsFunnel/components/GeneralInformationSection'
import { GenericDocumentsSection } from '@/features/publicCompanyLeadsFunnel/components/GenericDocumentsSection'
import { LeadHeader } from '@/features/publicCompanyLeadsFunnel/components/LeadHeader'
import { ResponsibleSection } from '@/features/publicCompanyLeadsFunnel/components/ResponsibleSection'
import {
  stateStrategy,
  translateStateStrategy,
} from '@/features/publicCompanyLeadsFunnel/constants/stateStrategy.constant'
import { useAdvancePublicCompanyLead } from '@/features/publicCompanyLeadsFunnel/hooks/useAdvancePublicCompanyLead'
import usePublicCompanyLeadForm from '@/features/publicCompanyLeadsFunnel/hooks/usePublicCompanyLeadForm'
import { useRegressPublicCompanyLead } from '@/features/publicCompanyLeadsFunnel/hooks/useRegressPublicCompanyLead'
import { useReviewSectionLead } from '@/features/publicCompanyLeadsFunnel/hooks/useReviewSectionLead'
import { PublicCompanyLeadsFunnelUpdateValidationSchema } from '@/features/publicCompanyLeadsFunnel/schemas/update.schema'
import { PublicCompanyLeadData } from '@/features/publicCompanyLeadsFunnel/types/publicCompanyLead'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { FormProvider, SubmitHandler } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useUpdatePublicCompanyLeadManagers } from '../../hooks/useUpdatePublicCompanyLeadManagers'
import { useUpdatePublicCompanyLead } from '../../hooks/useUpdatePublicCompanyLead'

const LeadQualifyingManagementReviewForm = ({
  id,
  publicCompanyLead,
}: {
  id: string
  publicCompanyLead: PublicCompanyLeadData
}) => {
  const queryClient = useQueryClient()
  const [file, setFile] = useState<Blob>()
  const [serverError, setServerError] = useState(false)
  const navigate = useNavigate()
  const passedEveryRequirement = publicCompanyLead?.requirement_list_summary?.steps?.every((step) => step.passed)
  const hasDisapprovedReview =
    publicCompanyLead.bidding_information?.last_review?.state === 'disapproved' ||
    publicCompanyLead.general_information?.last_review?.state === 'disapproved' ||
    publicCompanyLead.address?.last_review?.state === 'disapproved'

  const updateLeadOnSuccess = () => {
    setServerError(false)
    queryClient.invalidateQueries({ queryKey: ['publicCompanyLead', id] })
  }

  const redirectOnSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['publicCompanyLead', id] })
    navigate('/public-clients')
  }

  const onError = () => setServerError(true)

  const { mutate: advancePublicCompanyLead, isPending: advancePublicCompanyLeadIsPending } =
    useAdvancePublicCompanyLead({ id, onSuccess: redirectOnSuccess, onError })

  const { mutate: regressLead } = useRegressPublicCompanyLead({ id, onSuccess: redirectOnSuccess, onError })

  const { mutate: approveBiddingInformationLead } = useReviewSectionLead({
    publicCompanyLeadId: id,
    state: 'approved',
    section: 'bidding_information_attributes',
    onSuccess: updateLeadOnSuccess,
    onError,
  })

  const { mutate: declineBiddingInformationLead } = useReviewSectionLead({
    publicCompanyLeadId: id,
    state: 'disapproved',
    section: 'bidding_information_attributes',
    onSuccess: updateLeadOnSuccess,
    onError,
  })

  const { mutate: approveGeneralInformationLead } = useReviewSectionLead({
    publicCompanyLeadId: id,
    state: 'approved',
    section: 'general_information_attributes',
    onSuccess: updateLeadOnSuccess,
    onError,
  })

  const { mutate: declineGeneralInformationLead } = useReviewSectionLead({
    publicCompanyLeadId: id,
    state: 'disapproved',
    section: 'general_information_attributes',
    onSuccess: updateLeadOnSuccess,
    onError,
  })

  const { mutate: approveAddressLead } = useReviewSectionLead({
    publicCompanyLeadId: id,
    state: 'approved',
    section: 'address_attributes',
    onSuccess: updateLeadOnSuccess,
    onError,
  })

  const { mutate: declineAddressLead } = useReviewSectionLead({
    publicCompanyLeadId: id,
    state: 'disapproved',
    section: 'address_attributes',
    onSuccess: updateLeadOnSuccess,
    onError,
  })

  const useFormMethods = usePublicCompanyLeadForm({ publicCompanyLead })

  const { trigger, getValues, setError, clearErrors } = useFormMethods

  const { mutate: updatePublicCompanyLead } = useUpdatePublicCompanyLead({
    id,
    publicCompanyLead,
    file,
    onSuccess: updateLeadOnSuccess,
    onError: FormTransformers.errorsTransformer<PublicCompanyLeadsFunnelUpdateValidationSchema>({ setError }),
  })

  const {
    mutate: updatePublicCompanyLeadManagers,
    isPending: updatePublicCompanyLeadManagersIsPending,
  } = useUpdatePublicCompanyLeadManagers({
    id,
    onSuccess: updateLeadOnSuccess,
    onError: FormTransformers.errorsTransformer<PublicCompanyLeadsFunnelUpdateValidationSchema>({ setError }),
  });

  const submitManagers = async () => {
    updatePublicCompanyLeadManagers(getValues());
  };

  const onSubmit: SubmitHandler<PublicCompanyLeadsFunnelUpdateValidationSchema> = (data) => {
    clearErrors()
    updatePublicCompanyLead(data)
  }

  const submit = async () => {
    const isValid = await trigger()

    if (!isValid) {
      return
    }

    onSubmit(getValues())
  }

  return (
    <FormProvider {...useFormMethods}>
      <form>
        <LeadHeader
          publicCompanyLeadId={id}
          display_name={publicCompanyLead.display_name}
          url={publicCompanyLead.general_information?.company_logo?.url}
          showArchiveButton
          archived_at={publicCompanyLead.archived_at}
          buttons={
            <div className="flex gap-4">
              <Button
                type="button"
                disabled={!hasDisapprovedReview}
                variant={!hasDisapprovedReview ? 'yellow' : 'yellow-black'}
                onClick={() => regressLead()}
              >
                Solicitar revisão
              </Button>
              <Button
                name="send_to_sales"
                type="button"
                className={passedEveryRequirement ? 'shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]' : ''}
                variant={passedEveryRequirement ? 'default' : 'outline-disabled'}
                disabled={!passedEveryRequirement || advancePublicCompanyLeadIsPending}
                onClick={() => advancePublicCompanyLead()}
                isLoading={advancePublicCompanyLeadIsPending}
              >
                Enviar para Comercial
              </Button>
            </div>
          }
        />
        <div className="flex gap-6">
          <Box className="w-full flex flex-col gap-6 h-[calc(100vh-130px)] overflow-y-scroll">
            {serverError && <p className="text-sm text-danger-soft">Ocorreu um erro no servidor, tente novamente</p>}
            {publicCompanyLead.last_review?.state === 'disapproved' && (
              <RequestedReview description={publicCompanyLead.last_review?.description} />
            )}
            <ResponsibleSection
              publicCompanyLead={publicCompanyLead}
              submit={submitManagers}
              submitObservations={submit}
              disabled={advancePublicCompanyLeadIsPending || updatePublicCompanyLeadManagersIsPending}
            />
            <BiddingInformationSection
              publicCompanyLeadId={id}
              publicCompanyLeadState={publicCompanyLead.state}
              contractId={publicCompanyLead.contract?.id}
              contractFile={publicCompanyLead.contract?.file}
              bidding_information={publicCompanyLead.bidding_information}
              approveBiddingInformationLead={approveBiddingInformationLead}
              declineBiddingInformationLead={declineBiddingInformationLead}
              disabled
            />
            <GeneralInformationSection
              general_information={publicCompanyLead.general_information}
              file={file}
              setFile={setFile}
              approveGeneralInformationLead={approveGeneralInformationLead}
              declineGeneralInformationLead={declineGeneralInformationLead}
              disabled
            />
            <AddressSection
              address={publicCompanyLead.address}
              approveAddressLead={approveAddressLead}
              declineAddressLead={declineAddressLead}
              disabled
            />
            <GenericDocumentsSection
              publicCompanyLeadId={id}
              generic_documents={publicCompanyLead.generic_documents}
              disabled={advancePublicCompanyLeadIsPending}
            />
          </Box>
          <div className="flex flex-col gap-4">
            <Checklist
              requirement_list_summary={publicCompanyLead.requirement_list_summary}
              state={translateStateStrategy[stateStrategy[publicCompanyLead.state as keyof typeof stateStrategy]]}
              className="flex flex-col gap-2 justify-between h-[calc((100vh-276px)/2)]"
              Buttons={() => (
                <div className="flex flex-col gap-2">
                  <Button
                    type="button"
                    disabled={!hasDisapprovedReview}
                    variant={!hasDisapprovedReview ? 'yellow' : 'yellow-black'}
                    onClick={() => regressLead()}
                  >
                    Solicitar revisão
                  </Button>
                  <Button
                    type="button"
                    variant={passedEveryRequirement ? 'default' : 'outline-disabled'}
                    disabled={!passedEveryRequirement || advancePublicCompanyLeadIsPending}
                    onClick={() => advancePublicCompanyLead()}
                    isLoading={advancePublicCompanyLeadIsPending}
                  >
                    Enviar para Comercial
                  </Button>
                </div>
              )}
            />
            <ChangeHistory logs={publicCompanyLead?.logs || []} />
          </div>
        </div>
      </form>
    </FormProvider>
  )
}

export default LeadQualifyingManagementReviewForm
